import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Insight, Category } from './types';
import { PROMPTS, CATEGORIES } from './constants';
import {
    Container,
    Header,
    ShiftTimer,
    TimerHint,
    Section,
    Button,
    PromptText,
    CategoriesGrid,
    CategoryButton,
    CategoryIcon,
    TextArea,
    InsightsHeader,
    InsightsTitle,
    InsightsList,
    InsightItem,
    InsightHeader as StyledInsightHeader,
    InsightCategory,
    InsightTime,
    InsightContent,
    EmptyState,
    ButtonGroup
} from './styles';

function App() {
    const [currentDateTime, setCurrentDateTime] = useState(new Date());
    const [currentPrompt, setCurrentPrompt] = useState('Tap above for a reflection prompt');
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [note, setNote] = useState('');
    const [insights, setInsights] = useState<Insight[]>(() => {
        const saved = localStorage.getItem('clinicalInsights');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    const handleGetPrompt = () => {
        const randomPrompt = PROMPTS[Math.floor(Math.random() * PROMPTS.length)];
        setCurrentPrompt(randomPrompt);
    };

    const handleSaveInsight = () => {
        if (!note.trim()) {
            alert('Please enter an insight before saving');
            return;
        }

        if (!selectedCategory) {
            alert('Please select a category');
            return;
        }

        const newInsight: Insight = {
            id: Date.now(),
            timestamp: new Date().toISOString(),
            category: selectedCategory,
            note: note.trim()
        };

        const updatedInsights = [newInsight, ...insights].slice(0, 100);
        setInsights(updatedInsights);
        localStorage.setItem('clinicalInsights', JSON.stringify(updatedInsights));

        // Reset form
        setNote('');
        setSelectedCategory(null);
    };

    const handleExport = () => {
        if (insights.length === 0) {
            alert('No insights to export');
            return;
        }

        // Group insights by date
        const insightsByDate = insights.reduce((acc, insight) => {
            const date = format(new Date(insight.timestamp), 'yyyy-MM-dd');
            if (!acc[date]) {
                acc[date] = [];
            }
            acc[date].push(insight);
            return acc;
        }, {} as Record<string, Insight[]>);

        // Create formatted Markdown
        let exportText = '# Clinical Insights Export\n\n';
        exportText += `*Generated: ${format(new Date(), 'PPpp')}*\n\n`;
        exportText += `**Total Insights:** ${insights.length}\n\n`;
        exportText += '---\n\n';

        Object.entries(insightsByDate).forEach(([date, dateInsights]) => {
            exportText += `## 📅 ${format(new Date(date), 'PP')}\n\n`;
            
            dateInsights.forEach(insight => {
                const time = format(new Date(insight.timestamp), 'p');
                const category = CATEGORIES[insight.category];
                
                exportText += `### ${category.emoji} ${category.label}\n\n`;
                exportText += `- **Time:** ${time}\n\n`;
                exportText += `#### Insight\n\n`;
                exportText += `> ${insight.note}\n\n`;
                exportText += `---\n\n`;
            });
        });

        // Add summary section
        const categoryCounts = insights.reduce((acc, insight) => {
            acc[insight.category] = (acc[insight.category] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        exportText += `## 📊 Summary\n\n`;
        exportText += `| Category | Count |\n`;
        exportText += `|----------|-------|\n`;
        Object.entries(categoryCounts).forEach(([category, count]) => {
            const categoryConfig = CATEGORIES[category];
            exportText += `| ${categoryConfig.emoji} ${categoryConfig.label} | ${count} |\n`;
        });

        navigator.clipboard.writeText(exportText).then(() => {
            alert('Insights exported to clipboard!');
        }).catch(() => {
            alert('Failed to copy to clipboard. Please try again.');
        });
    };

    const handleClear = () => {
        if (insights.length === 0) {
            alert('No insights to clear');
            return;
        }

        const confirmClear = window.confirm(`Are you sure you want to clear all ${insights.length} insights? This cannot be undone.`);
        
        if (confirmClear) {
            setInsights([]);
            localStorage.setItem('clinicalInsights', JSON.stringify([]));
        }
    };

    return (
        <Container>
            <Header>
                <ShiftTimer>
                    {format(currentDateTime, 'EEE, MMM d • h:mm a')}
                </ShiftTimer>
                <TimerHint>Current Date & Time</TimerHint>
            </Header>

            <Section>
                <Button onClick={handleGetPrompt}>Get Random Prompt</Button>
                <PromptText>{currentPrompt}</PromptText>
            </Section>

            <Section>
                <CategoriesGrid>
                    {Object.entries(CATEGORIES).map(([key, { label, emoji }]) => (
                        <CategoryButton
                            key={key}
                            selected={selectedCategory === key}
                            onClick={() => setSelectedCategory(key as Category)}
                        >
                            <CategoryIcon>{emoji}</CategoryIcon>
                            <span>{label}</span>
                        </CategoryButton>
                    ))}
                </CategoriesGrid>
                <TextArea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Capture your insight here..."
                />
                <Button variant="success" onClick={handleSaveInsight}>
                    Save Insight
                </Button>
            </Section>

            <Section>
                <InsightsHeader>
                    <InsightsTitle>Recent Insights</InsightsTitle>
                    <ButtonGroup>
                        <Button onClick={handleExport}>Export All</Button>
                        <Button variant="danger" onClick={handleClear}>Clear</Button>
                    </ButtonGroup>
                </InsightsHeader>
                <InsightsList>
                    {insights.length === 0 ? (
                        <EmptyState>No insights captured yet</EmptyState>
                    ) : (
                        insights.slice(0, 10).map((insight) => {
                            const category = CATEGORIES[insight.category];
                            return (
                                <InsightItem key={insight.id}>
                                    <StyledInsightHeader>
                                        <InsightCategory>
                                            {category.emoji} {category.label}
                                        </InsightCategory>
                                        <InsightTime>
                                            {format(new Date(insight.timestamp), 'MMM d • h:mm a')}
                                        </InsightTime>
                                    </StyledInsightHeader>
                                    <InsightContent>{insight.note}</InsightContent>
                                </InsightItem>
                            );
                        })
                    )}
                </InsightsList>
            </Section>
        </Container>
    );
}

export default App;
